import { useState } from "react";
import { Note } from "@/sharedTypes";
import { Button, Textarea, Checkbox, Spinner } from "flowbite-react";
import { HiPencilAlt, HiTrash, HiCheck, HiX } from "react-icons/hi";
import { useDeleteNoteMutation, useUpdateNoteMutation } from "@/api/note";
import ConfirmationDialog from "@/components/ConfirmationDialog";

function NoteItem({ note }: { note: Note }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedNote, setEditedNote] = useState(note.note_body);
  const [isVisibleToClient, setIsVisibleToClient] = useState(
    note.is_visible_to_client,
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleEditToggle = () => {
    setIsEditing((prev) => !prev);
  };

  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedNote(e.target.value);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsVisibleToClient(e.target.checked);
  };

  //Save handlers
  const [updateNote, { isLoading }] = useUpdateNoteMutation();

  const handleSave = () => {
    console.log("Save note:", note.note_id, editedNote, isVisibleToClient);
    updateNote({
      noteId: note.note_id as number,
      noteData: {
        note_body: editedNote,
        is_visible_to_client: isVisibleToClient,
      },
    })
      .unwrap()
      .then(() => {
        setIsEditing(false);
      })
      .catch((error) => {
        console.error("Error updating note:", error);
      });
  };

  const handleCancel = () => {
    setEditedNote(note.note_body);
    setIsVisibleToClient(note.is_visible_to_client);
    setIsEditing(false);
  };

  //Deletion handlers

  const [deleteNote, { isLoading: isDeleting }] = useDeleteNoteMutation();

  const confirmDelete = () => {
    deleteNote(note.note_id as number)
      .unwrap()
      .then(() => {
        setShowDeleteDialog(false);
      })
      .catch((error) => {
        console.error("Error deleting note:", error);
      });
  };

  return (
    <>
      <div className="flex w-full items-center justify-between">
        <div className="mb-4 w-full border-b pb-2">
          {isEditing ? (
            <>
              <Textarea
                value={editedNote}
                onChange={handleNoteChange}
                className="mb-4 w-full rounded border p-2"
                rows={4}
              />
              <div className="mt-2 flex items-center">
                <Checkbox
                  id={`visibleToClient-${note.note_id}`}
                  checked={isVisibleToClient}
                  onChange={handleCheckboxChange}
                  className="mr-2"
                />
                <label
                  htmlFor={`visibleToClient-${note.note_id}`}
                  className="text-sm text-gray-800 dark:text-gray-200"
                >
                  Is visible to client
                </label>
              </div>
            </>
          ) : (
            <>
              <p className="mb-2 text-base text-gray-800 dark:text-gray-200">
                {note.note_body}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Added by {note.agent_username} on{" "}
                {new Date(note.createdAt).toLocaleString()}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Visible to client: {note.is_visible_to_client ? "Yes" : "No"}
              </p>
            </>
          )}
        </div>
        <div className="flex space-x-2">
          {isEditing ? (
            <div className="mb-14 flex gap-2">
              {isLoading ? (
                <Spinner className="ml-2" />
              ) : (
                <>
                  <button
                    className="p-1 text-green-500 hover:text-green-700"
                    onClick={handleSave}
                    title="Save"
                    disabled={isLoading}
                  >
                    <HiCheck className="h-5 w-5" />
                  </button>
                  <button
                    className="p-1 text-gray-500 hover:text-gray-700"
                    onClick={handleCancel}
                    title="Cancel"
                    disabled={isLoading}
                  >
                    <HiX className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              <button
                className="p-1 text-purple-500 hover:text-purple-700"
                onClick={handleEditToggle}
                title="Edit"
              >
                <HiPencilAlt className="h-5 w-5" />
              </button>
              <button
                className="p-1 text-red-500 hover:text-red-700"
                onClick={() => setShowDeleteDialog(true)}
                title="Delete"
              >
                <HiTrash className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </div>
      {showDeleteDialog && (
        <ConfirmationDialog
          title="Confirm Deletion"
          body="Are you sure you want to delete this note?"
          onCancel={() => setShowDeleteDialog(false)}
          onConfirm={confirmDelete}
          isLoading={isDeleting}
        />
      )}
    </>
  );
}

export default NoteItem;
